import * as React from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { roomSelector, brandingSelector } from "../../../recoil/atoms/room"
import Switch from "../../common/switch"
import { SDKContext } from "../../../context/SDKContext"
import type {
  ModerationSettingsItemType,
  initialVideoResolutionOnMobileOptions
} from "./moderation.interfaces"
import {
  ModerationWrapper,
  ModerationContainer,
  ModerationTextTip,
  ModerationTitle,
  ModerationSubTitle,
  ModerationSettingsList,
  ModerationSettingsItem,
  ModerationSettingsName,
  CheckBoxesGroup,
  Container
} from "./moderation.styled"
import { hasPermission } from "../../../util"
import { overlaySelector } from "../../../recoil/atoms/overlay"
import { SecondaryButton } from "../../common/buttons/secondary"
import {
  notificationNewUsersSelector,
  notificationNewMessagesSelector
} from "../../../recoil/atoms/notification"
import { CustomCheckbox } from "../../common/checkbox"
import { initialVideoResolutionOnMobileValues } from "../../../constants/enums"
import ModerationLinks from "./moderation-links"
import { useTranslation } from "react-i18next"
import HighQualityStreamSettings from "./HighQualityStreamSettings"

const UserPermission = window.MeetOnline?.UserPermission

const Moderation = React.memo(() => {
  const { t } = useTranslation()
  const meetingClient = React.useContext(SDKContext)
  const room = useRecoilValue(roomSelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const { isEnabledNotificationNewUsers } = useRecoilValue(
    notificationNewUsersSelector
  )
  const { isEnabledNotificationNewMessages } = useRecoilValue(
    notificationNewMessagesSelector
  )
  const setNotificationNewUsersParams = useSetRecoilState(
    notificationNewUsersSelector
  )
  const setNotificationNewMessagesParams = useSetRecoilState(
    notificationNewMessagesSelector
  )

  const currentOverlay = useRecoilValue(overlaySelector)
  const audiencePermissions = React.useMemo(
    () => +room.audiencePermissions,
    [room.audiencePermissions]
  )

  const togglePermission = React.useCallback(
    (permission: number) => {
      if (hasPermission(audiencePermissions, permission)) {
        meetingClient?.updatingAllBroadcasterPermissions({
          removePermissions: permission
        })
        meetingClient?.updatingAllAudiencePermissions({
          removePermissions: permission
        })
      }

      if (!hasPermission(audiencePermissions, permission)) {
        meetingClient?.updatingAllBroadcasterPermissions({
          addPermissions: permission
        })
        meetingClient?.updatingAllAudiencePermissions({
          addPermissions: permission
        })
      }
    },
    [audiencePermissions, meetingClient]
  )

  const settingsListAudience = React.useMemo<ModerationSettingsItemType[]>(
    () => [
      {
        id: "1",
        text: t("See the list of participants"),
        checked: hasPermission(
          audiencePermissions,
          UserPermission.canSeeUserList
        ),
        permission: UserPermission.canSeeUserList
      },
      {
        id: "2",
        text: t("Use text chat"),
        checked: hasPermission(audiencePermissions, UserPermission.canUseChat),
        permission: UserPermission.canUseChat
      },
      {
        id: "3",
        text: t("See shop with featured products"), // TODO: Add translation remove old "See a history of displayed items"
        checked: hasPermission(
          audiencePermissions,
          UserPermission.canSeeHistory
        ),
        permission: UserPermission.canSeeHistory
      },
      {
        id: "4",
        text: t("Use a 'raise hand' button"),
        checked: hasPermission(
          audiencePermissions,
          UserPermission.canRaiseHand
        ),
        permission: UserPermission.canRaiseHand
      },
      {
        id: "5",
        text: t("Send heart emojis to screen"),
        checked: hasPermission(audiencePermissions, UserPermission.canUseEmoji),
        permission: UserPermission.canUseEmoji
      }
    ],
    [audiencePermissions, t]
  )

  const handleToggleModeratorRequired = React.useCallback(
    (status: boolean): void => {
      if (status) {
        meetingClient?.toggleModeratorApproved(false)
      } else {
        meetingClient?.toggleModeratorApproved(true)
      }
    },
    [meetingClient]
  )

  const handleAllowChatWhenNoPresenter = React.useCallback(
    (status: boolean): void => {
      if (status) {
        meetingClient?.toggleAllowChatWhenNoPresenter(false)
      } else {
        meetingClient?.toggleAllowChatWhenNoPresenter(true)
      }
    },
    [meetingClient]
  )

  const handleToggleProfanityFilter = React.useCallback(
    (status: boolean): void => {
      if (status) {
        meetingClient?.toggleProfanityFilter(false)
      } else {
        meetingClient?.toggleProfanityFilter(true)
      }
    },
    [meetingClient]
  )

  const handleToggleChatRequired = React.useCallback(
    (status: boolean): void => {
      if (status) {
        meetingClient?.toggleChatRequired(false)
      } else {
        meetingClient?.toggleChatRequired(true)
      }
    },
    [meetingClient]
  )

  const handleToggleAlertNewUser = React.useCallback(
    (status: boolean): void => {
      setNotificationNewUsersParams({
        isEnabledNotificationNewUsers: !isEnabledNotificationNewUsers
      })
    },
    [isEnabledNotificationNewUsers, setNotificationNewUsersParams]
  )

  const handleToggleAlertNewMessage = React.useCallback(
    (status: boolean): void => {
      setNotificationNewMessagesParams({
        isEnabledNotificationNewMessages: !isEnabledNotificationNewMessages
      })
    },
    [isEnabledNotificationNewMessages, setNotificationNewMessagesParams]
  )

  const changeInitialVideoResolutionOnMobile = React.useCallback(
    (value: string | number) => {
      meetingClient?.changeInitialVideoResolutionOnMobile(value)
    },
    [meetingClient]
  )

  const changeChatInputBoxOverlay = React.useCallback(
    (value: boolean) => {
      meetingClient?.changeChatInputBoxOverlay(value)
    },
    [meetingClient]
  )

  const settingsListGeneral = React.useMemo<ModerationSettingsItemType[]>(
    () => [
      {
        id: "6",
        text: t("Presenter must be present"),
        checked: room.moderatorRequired,
        callback: handleToggleModeratorRequired
      },
      ...(room.moderatorRequired
        ? [
            {
              id: "7",
              text: t("Allow text chat when no presenters"),
              checked: room.allowChatWhenNoPresenter,
              callback: handleAllowChatWhenNoPresenter
            }
          ]
        : []),
      {
        id: "8",
        text: t("Chat must be moderator approved"),
        checked: room.chatApprovalRequired,
        callback: handleToggleChatRequired
      },
      {
        id: "9",
        text: t("Profanity filter"),
        checked: room.enableProfanityFilter,
        callback: handleToggleProfanityFilter
      },
      {
        id: "10",
        text: t("New member alert sound"),
        checked: isEnabledNotificationNewUsers,
        callback: handleToggleAlertNewUser
      },
      {
        id: "11",
        text: t("New message alert sound"),
        checked: isEnabledNotificationNewMessages,
        callback: handleToggleAlertNewMessage
      }
    ],
    [
      t,
      room.moderatorRequired,
      room.allowChatWhenNoPresenter,
      room.chatApprovalRequired,
      room.enableProfanityFilter,
      handleToggleModeratorRequired,
      handleAllowChatWhenNoPresenter,
      handleToggleChatRequired,
      handleToggleProfanityFilter,
      isEnabledNotificationNewUsers,
      handleToggleAlertNewUser,
      isEnabledNotificationNewMessages,
      handleToggleAlertNewMessage
    ]
  )

  const settingsClientSetting = React.useMemo<ModerationSettingsItemType[]>(
    () => [
      {
        id: "12",
        text: t("Chat input box overlay"),
        checked: room.chatInputBoxOverlay,
        callback: changeChatInputBoxOverlay
      }
    ],
    [t, room.chatInputBoxOverlay, changeChatInputBoxOverlay]
  )

  const initialVideoResolutionOnMobileOptions = React.useMemo<
    initialVideoResolutionOnMobileOptions[]
  >(
    () => [
      {
        id: "13",
        value: initialVideoResolutionOnMobileValues.REGULAR_16_9,
        label: "16:9",
        checked:
          room.initialVideoResolutionMobilePortrait ===
          initialVideoResolutionOnMobileValues.REGULAR_16_9,
        callback: changeInitialVideoResolutionOnMobile
      },
      {
        id: "14",
        value: initialVideoResolutionOnMobileValues.SQUARE,
        label: t("Square"),
        checked:
          room.initialVideoResolutionMobilePortrait ===
          initialVideoResolutionOnMobileValues.SQUARE,
        callback: changeInitialVideoResolutionOnMobile
      },
      {
        id: "15",
        value: initialVideoResolutionOnMobileValues.FULLSCREEN,
        label: t("Full Screen"),
        checked:
          room.initialVideoResolutionMobilePortrait ===
          initialVideoResolutionOnMobileValues.FULLSCREEN,
        callback: changeInitialVideoResolutionOnMobile
      }
    ],
    [
      changeInitialVideoResolutionOnMobile,
      room.initialVideoResolutionMobilePortrait,
      t
    ]
  )

  const handleClearChat = React.useCallback(() => {
    if (currentOverlay?.type === "chat") {
      meetingClient?.unPublishOverlay(currentOverlay?.id)
    }
    meetingClient?.clearChat()
  }, [currentOverlay?.id, currentOverlay?.type, meetingClient])

  const handleClearBadgeHistory = () => {
    meetingClient?.clearBadgeHistory()
  }

  const handleSwitch = React.useCallback(setting => {
    setting.callback(setting.checked)
  }, [])

  return (
    <Container>
      <ModerationWrapper roomStyles={roomStyles}>
        <ModerationContainer roomStyles={roomStyles}>
          <ModerationTitle roomStyles={roomStyles}>
            {t("Moderation controls")}
          </ModerationTitle>

          <ModerationSubTitle
            style={{ color: roomStyles?.servicePanelElementSecondaryText }}
          >
            {t("LET MY AUDIENCE & BROADCASTERS")}
          </ModerationSubTitle>

          <ModerationSettingsList>
            {settingsListAudience.map(setting => (
              <ModerationSettingsItem key={setting.id}>
                <ModerationSettingsName roomStyles={roomStyles}>
                  {setting.text}
                </ModerationSettingsName>
                <Switch
                  checked={setting.checked}
                  onChange={e => {
                    togglePermission(setting.permission)
                  }}
                />
              </ModerationSettingsItem>
            ))}
          </ModerationSettingsList>
          <ModerationSubTitle
            style={{ color: roomStyles?.servicePanelElementSecondaryText }}
          >
            {t("GENERAL")}
          </ModerationSubTitle>
          <ModerationSettingsList>
            {settingsListGeneral.map(setting => (
              <ModerationSettingsItem key={setting.id}>
                <ModerationSettingsName
                  roomStyles={roomStyles}
                  fontStyle={setting.id === "7" ? "italic" : undefined}
                >
                  {setting.text}
                </ModerationSettingsName>
                <Switch
                  checked={setting.checked}
                  onChange={e => handleSwitch(setting)}
                />
              </ModerationSettingsItem>
            ))}
          </ModerationSettingsList>

          <ModerationSubTitle
            style={{ color: roomStyles?.servicePanelElementSecondaryText }}
          >
            {t("ADVANCED CLIENT SETTINGS")}
          </ModerationSubTitle>
          <ModerationSettingsList>
            <ModerationSettingsItem key={10001}>
              <ModerationSettingsName roomStyles={roomStyles}>
                {t("Initial video resolution on mobile")}
              </ModerationSettingsName>
            </ModerationSettingsItem>
            <CheckBoxesGroup>
              {initialVideoResolutionOnMobileOptions.map((option, index) => (
                <CustomCheckbox
                  key={`initialVideoResolutionOnMobile-${index}`}
                  changingplaces="true"
                  fontSize="0.8125rem"
                  ratiobutton="true"
                  label={option.label}
                  marginRight="0.7rem"
                  value={option.value}
                  isChecked={option.checked}
                  onChange={() => option.callback(option.value)}
                  radiobutton={true}
                  _last={{ marginRight: "0rem" }}
                />
              ))}
            </CheckBoxesGroup>
            {settingsClientSetting.map(setting => (
              <ModerationSettingsItem key={setting.id}>
                <ModerationSettingsName roomStyles={roomStyles}>
                  {setting.text}
                </ModerationSettingsName>
                <Switch
                  checked={setting.checked}
                  onChange={e => handleSwitch(setting)}
                />
              </ModerationSettingsItem>
            ))}
          </ModerationSettingsList>
          <HighQualityStreamSettings />
          <ModerationLinks />
          <SecondaryButton
            text={"Clear chat history"}
            onClick={handleClearChat}
            style={{ marginBottom: "0.4rem" }}
            width={"100%"}
          />

          <SecondaryButton
            text={"Clear badge history"}
            onClick={handleClearBadgeHistory}
            width={"100%"}
            marginBottom="10px"
          />

          <ModerationSubTitle
            style={{ color: roomStyles?.servicePanelElementSecondaryText }}
          >
            {t("Individual audience member settings")}
          </ModerationSubTitle>
          <ModerationTextTip roomStyles={roomStyles}>
            {t(
              "You can give individual audience members permission to turn on their microphones and cameras or share their screens by selecting them from the participant list."
            )}
          </ModerationTextTip>
        </ModerationContainer>
      </ModerationWrapper>
    </Container>
  )
})

export default Moderation
